import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import TrialBanner from './subscription/TrialBanner';
import ParentGate from './school/ParentGate';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import Mark from './components/Mark';
import { groupLinks, usePrimaryLinks } from './components/usePrimaryLinks';
import NavGroup from './components/NavGroup';
import { useAuth } from './auth/AuthContext';
import { DESIGN_PALETTE, useSettings } from './settings/SettingsContext';
import { useLearner } from './learners/LearnerContext';
import { api } from './api/client';
import PinDialog from './components/PinDialog';
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import SsoCallbackPage from './pages/SsoCallbackPage';
import SchoolPage from './school/SchoolPage';
import SpeechWarmer from './programs/SpeechWarmer';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HomePage from './pages/HomePage';
import GameRequestPage from './pages/GameRequestPage';
import PromoAdminPage from './pages/PromoAdminPage';
import TestersPage from './pages/TestersPage';
import DpaAdminPage from './pages/DpaAdminPage';
import StudioPage from './pages/StudioPage';
import AiPage from './pages/AiPage';
import AssistHome from './assist/AssistHome';
import ExpertsPage from './assist/ExpertsPage';
import ExpertWorkspace from './assist/ExpertWorkspace';
import AssistAdmin from './assist/AssistAdmin';
import AssistPolicyPage from './assist/AssistPolicyPage';
import LearnPage from './guides/LearnPage';
import GuidePage from './guides/GuidePage';
import ActingBanner from './assist/ActingBanner';
import SettingsPage from './pages/SettingsPage';
import SubscribePage from './pages/SubscribePage';
import SubscriptionPage from './pages/SubscriptionPage';
import PlannerPage from './planner/PlannerPage';
import NowNextPage from './planner/NowNextPage';
import ProgressPage from './pages/ProgressPage';
import ReportPage from './pages/ReportPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsGate from './terms/TermsGate';
import ConsentGate from './learners/ConsentGate';
import TwoFactorPrompt from './settings/TwoFactorPrompt';
import PreferencePage from './pages/PreferencePage';
import RewardsPage from './pages/RewardsPage';
import InvitedPage from './pages/InvitedPage';
import UserMenu from './components/UserMenu';
import DeleteAccountDialog from './components/DeleteAccountDialog';
import { screenOf } from './designs/screen';
import ProgramEditorPage from './pages/ProgramEditorPage';
import PlayPage from './pages/PlayPage';
import PackagesPage from './pages/PackagesPage';
import LibraryPage from './concepts/LibraryPage';
import LibraryPreviewPage from './concepts/LibraryPreviewPage';
import TalkBoardPage from './talk/TalkBoardPage';
import { GamePlay, GamesList } from './leisure/GamesPage';
import TalkLauncher from './talk/TalkLauncher';
import GameTypesAdminPage from './pages/GameTypesAdminPage';
import { loadAddons } from './games/host/addons';
import Backdrop from './designs/liquid/Backdrop';
import LiquidMark from './designs/liquid/LiquidMark';
import { motionAllowed } from './designs/scene/programs';
import { useEntitlements } from './subscription/offers';
import { useTight } from './components/useTight';
import { useT } from './i18n/i18n';

function RequireAuth({ children }: { children: ReactNode }) {
  const { token, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return null;
  if (!token) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  /* EVERY SIGNED-IN SCREEN, including the checkout one. Agreeing to the terms is
     not something to ask for only once somebody is paying. */
  return <TermsGate><ConsentGate><TwoFactorPrompt>{children}</TwoFactorPrompt></ConsentGate></TermsGate>;
}

/**
 * THIS USED TO RENDER NOTHING AND WAIT FOREVER.
 *
 * Null means "not asked yet", which is not "not subscribed" — bouncing to the
 * checkout page on it would throw a paying supervisor out on every cold load.
 * But returning null while waiting meant that if the answer never arrived, the
 * page simply stayed empty: no message, no error, no way forward, with the nav
 * bar still sitting above it because that only needs a signed-in user. A
 * reload fixed it, which is precisely what makes such a fault hard to report.
 *
 * So there are now three states rather than two, and every one of them draws
 * something: waiting, failed-and-retryable, and answered.
 */
function RequireSubscription({ children }: { children: ReactNode }) {
  const { subscriptionStatus, subscriptionFailed, refreshSubscription } = useAuth();
  const entitlements = useEntitlements();
  const t = useT();

  /* ASK AGAIN IF NOBODY EVER DID. The status is fetched at sign-in and on
     reload, and if that attempt failed there was nothing to try it a second
     time. The screen that needs the answer asks for it itself. */
  useEffect(() => {
    if (subscriptionStatus === null && !subscriptionFailed) {
      void refreshSubscription();
    }
  }, [subscriptionStatus, subscriptionFailed, refreshSubscription]);

  if (subscriptionStatus === null) {
    if (!subscriptionFailed) {
      return <p className="muted" role="status">{t('Loading…')}</p>;
    }
    return (
      <div className="card" role="alert">
        <h2>{t('We could not check your subscription')}</h2>
        <p className="field-hint">
          {t('This is usually a connection problem rather than anything wrong with your account.')}
        </p>
        <div className="toolbar">
          <button className="btn" type="button" onClick={() => void refreshSubscription()}>
            {t('Try again')}
          </button>
        </div>
      </div>
    );
  }

  const ok = subscriptionStatus === 'ACTIVE' || subscriptionStatus === 'TRIALING';
  if (entitlements === null) return <p className="muted" role="status">{t('Loading…')}</p>;
  if (!ok && !entitlements.main) return <Navigate to={entitlements.talkBoard ? '/talk' : '/subscribe'} replace />;
  if (!entitlements.main) return <Navigate to={entitlements.talkBoard ? '/talk' : '/subscribe'} replace />;
  return <>{children}</>;
}

type SettingsResponse = Partial<import('./settings/SettingsContext').UserSettings>;

/**
 * WHO THE PAGES ARE ABOUT, ON ITS OWN STRIP.
 *
 * <p>Home and the week each had their own picker reading the same remembered value, so
 * changing it on one left the other showing the previous learner until something reloaded it.
 * One control, above every page.
 *
 * <p>NOT AN ITEM IN THE MENU, which is where it started. A form control among navigation links
 * belongs to neither: the links are pills that go somewhere, and this sets something. It says
 * out loud what it is instead of hiding its label.
 *
 * <p>Gone while a learner is playing — the board strips the chrome to one Home link on
 * purpose, and a switcher there is both a distraction and a way for a learner to change
 * themselves. Gone on a single-learner account, where it is a question with one answer.
 */
function AddonPreloader() {
  const { token } = useAuth();
  useEffect(() => { if (token) void loadAddons(); }, [token]);
  return null;
}

function LearnerBar() {
  const { user } = useAuth();
  const { learners, learnerId, select } = useLearner();
  const loc = useLocation();
  const t = useT();

  if (!user || loc.pathname.startsWith('/play/') || loc.pathname.startsWith('/studio') || learners.length < 2) return null;

  return (
    <div className="learner-bar">
      <label className="learner-bar-label" htmlFor="learner-bar-select">{t('Working with')}</label>
      <select
        id="learner-bar-select"
        value={learnerId ?? ''}
        onChange={(e) => select(e.target.value)}
      >
        {learners.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
}

function ThemeAttributes() {
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const screen = screenOf(pathname);
  const design = screen === 'game' ? settings.gameDesign : settings.design;
  const palette = settings.palette === 'theme' ? DESIGN_PALETTE[design] : settings.palette;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-screen', screen);
    root.setAttribute('data-design', design);
    root.setAttribute('data-palette', palette);
  }, [screen, design, palette]);

  return null;
}

function TopBar() {
  const { user, token } = useAuth();
  const { settings, applyServerSettings } = useSettings();
  const loc = useLocation();
  const nav = useNavigate();
  const [deleting, setDeleting] = useState(false);
  // While playing a program we keep the chrome minimal — only the Home link is offered
  // so the learner can leave; Settings and Sign out are intentionally hidden.
  const isPlaying = loc.pathname.startsWith('/play/');
  const links = usePrimaryLinks();
  // Guard the Home link with a supervisor PIN while a learner is playing — stops a student
  // from accidentally tapping out of the game. If no PIN is configured the link works normally.
  const guardHome = isPlaying && settings.supervisorPinSet;
  const [pinOpen, setPinOpen] = useState(false);
  const [shown, setShown] = useState(false);
  useEffect(() => { setShown(false); }, [loc.pathname]);
  const minimised = isPlaying && !shown;
  const barRef = useRef<HTMLElement | null>(null);
  const tight = useTight(barRef, [minimised]);
  const t = useT();

  function handleHomeClick(e: MouseEvent<HTMLAnchorElement>) {
    if (!guardHome) return;
    e.preventDefault();
    setPinOpen(true);
  }

  async function verifyPin(pin: string): Promise<boolean> {
    try {
      await api<void>('/me/supervisor-pin/verify', { method: 'POST', token, body: { pin } });
      return true;
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 401) return false;
      if (status === 429) throw new Error(t('Too many wrong PINs. Wait 5 minutes, then try again.'));
      throw err;
    }
  }

  // Forgot-PIN escape hatch: the supervisor proves it is them with their account
  // password — checked by the realm, not here — and the PIN is cleared.
  // After this succeeds they land on Home and can configure a new PIN in Settings.
  async function resetWithPassword(password: string): Promise<boolean> {
    try {
      const updated = await api<SettingsResponse>('/me/supervisor-pin/reset', {
        method: 'POST',
        token,
        body: { password, newPin: null },
      });
      applyServerSettings(updated);
      return true;
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 401) return false;
      throw err;
    }
  }

  return (
    <header ref={barRef} className={minimised ? 'topbar topbar-min' : tight ? 'topbar is-tight' : 'topbar'} role="banner">
      {minimised ? (
        <button
          type="button"
          className="btn ghost topbar-toggle"
          aria-expanded={false}
          onClick={() => setShown(true)}
        >
          {t('Menu')}
        </button>
      ) : (
      <>
      {/* aria-hidden on the mark: it sits beside the word it depicts, so
          announcing it would just repeat "EasyLearning" to a screen reader */}
      <h1 className="brand">
        {settings.design === 'chrome' && !isPlaying
          ? <LiquidMark moving={motionAllowed(settings.reduceMotion)} />
          : <Mark className="brand-mark" />}
        EasyLearning
      </h1>
      <nav aria-label={t('Primary')}>
        {user && (
          <>
            {links.some((l) => l.to === '/') && (
              <NavLink
                to="/"
                end
                onClick={handleHomeClick}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                {t('Home')}
              </NavLink>
            )}
            {isPlaying && (
              <button
                type="button"
                className="btn ghost topbar-toggle"
                aria-expanded={true}
                onClick={() => setShown(false)}
              >
                {t('Hide')}
              </button>
            )}
            {!isPlaying && groupLinks(links.filter((l) => l.to !== '/')).map((entry) => (
              'group' in entry
                ? <NavGroup key="programs" label={t('Programs')} links={entry.group.map((l) => ({ to: l.to, label: t(l.label) }))} />
                : <NavLink key={entry.to} to={entry.to} className={({ isActive }) => (isActive ? 'active' : '')}>{t(entry.label)}</NavLink>
            ))}
          </>
        )}
      </nav>
      {user && !isPlaying && <UserMenu onDeleteAccount={() => setDeleting(true)} />}
      </>
      )}
      <PinDialog
        open={pinOpen}
        onVerify={verifyPin}
        onForgotSubmit={resetWithPassword}
        onSuccess={() => { setPinOpen(false); nav('/'); }}
        onCancel={() => setPinOpen(false)}
      />
      <DeleteAccountDialog open={deleting} onClose={() => setDeleting(false)} />
    </header>
  );
}

export default function App() {
  const routes = (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
          {/* Public on purpose: whoever opens this cannot sign in yet, which is
              the very thing they are here to put right. */}
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Public for the same reason: whoever is reading this to decide
              whether to sign up does not have an account yet. */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/assist/terms" element={<AssistPolicyPage policy="terms" />} />
          <Route path="/assist/consultant-agreement" element={<AssistPolicyPage policy="agreement" />} />
          {/* PUBLIC ON PURPOSE, AND IT MATTERS HERE. Whoever follows an
              invitation very often has no account yet; behind RequireAuth they
              would be bounced to sign-in and the token in the URL would be
              thrown away, leaving them back at a page with nothing to accept.
              The page keeps the token across signing in itself. */}
          <Route path="/invited" element={<InvitedPage />} />
          <Route
            path="/studio"
            element={
              <RequireAuth>
                <RequireSubscription><StudioPage /></RequireSubscription>
              </RequireAuth>
            }
          />
          <Route path="/ai" element={<Navigate to="/assist/ai" replace />} />
          <Route path="/assist" element={<RequireAuth><RequireSubscription><AssistHome /></RequireSubscription></RequireAuth>} />
          <Route path="/assist/ai" element={<RequireAuth><RequireSubscription><AiPage /></RequireSubscription></RequireAuth>} />
          <Route path="/assist/experts" element={<RequireAuth><RequireSubscription><ExpertsPage /></RequireSubscription></RequireAuth>} />
          <Route path="/assist/expert" element={<RequireAuth><ExpertWorkspace /></RequireAuth>} />
          <Route path="/assist/admin" element={<RequireAuth><AssistAdmin /></RequireAuth>} />
          <Route path="/assist/learn" element={<RequireAuth><RequireSubscription><LearnPage /></RequireSubscription></RequireAuth>} />
          <Route path="/assist/learn/:slug" element={<RequireAuth><RequireSubscription><GuidePage /></RequireSubscription></RequireAuth>} />
          <Route
            path="/admin/promo"
            element={
              <RequireAuth>
                <PromoAdminPage />
              </RequireAuth>
            }
          />
          <Route path="/admin/game-types" element={<RequireAuth><GameTypesAdminPage /></RequireAuth>} />
          <Route
            path="/admin/dpa"
            element={
              <RequireAuth>
                <DpaAdminPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/testers"
            element={
              <RequireAuth>
                <TestersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/request-game"
            element={
              <RequireAuth>
                <RequireSubscription><GameRequestPage /></RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/subscribe"
            element={
              <RequireAuth>
                <SubscribePage />
              </RequireAuth>
            }
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <HomePage />
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/packages"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ParentGate need="edit"><PackagesPage /></ParentGate>
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route path="/games" element={<RequireAuth><GamesList /></RequireAuth>} />
          <Route path="/games/:id" element={<RequireAuth><GamePlay /></RequireAuth>} />
          <Route
            path="/account/subscription"
            element={
              <RequireAuth>
                <SubscriptionPage />
              </RequireAuth>
            }
          />
          <Route
            path="/plan"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <PlannerPage />
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/plan/now/:sessionId"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <NowNextPage />
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/preferences"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <PreferencePage />
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/rewards"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <RewardsPage />
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route path="/school" element={<RequireAuth><SchoolPage /></RequireAuth>} />
          <Route
            path="/reports"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ReportPage />
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/progress/:programId"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ProgressPage />
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/programs/new"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ParentGate need="edit"><ProgramEditorPage /></ParentGate>
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/programs/:id/edit"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ParentGate need="edit"><ProgramEditorPage /></ParentGate>
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/play/:id"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ParentGate need="play"><PlayPage /></ParentGate>
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/library"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ParentGate need="edit"><LibraryPage /></ParentGate>
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/library/:id"
            element={
              <RequireAuth>
                <RequireSubscription>
                  <ParentGate need="edit"><LibraryPreviewPage /></ParentGate>
                </RequireSubscription>
              </RequireAuth>
            }
          />
          <Route
            path="/talk"
            element={
              <RequireAuth>
                <ParentGate need="talk"><TalkBoardPage /></ParentGate>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );

  return (
    <div className="app-shell">
      <ThemeAttributes />
      <Backdrop />
      <TopBar />
      <SpeechWarmer />
      <ActingBanner />
      <LearnerBar />
      <TrialBanner />
      <main>{routes}</main>
      <AddonPreloader />
      <TalkLauncher />
    </div>
  );
}
